/**
 * User-activity (idle) tracking for **auto-away presence**.
 *
 * The presence status is `active` / `away` / `dnd` (see {@link RealtimeProvider}).
 * Today the only way to become "away" is to pick it by hand — so an idle user
 * who walked away still shows a green "active" dot to teammates, which is the
 * opposite of useful. This watcher closes that gap: after {@link AUTO_AWAY_IDLE_MS}
 * with no input it fires `onIdle` (the provider flips an *active* user to
 * "away"), and the very next input fires `onActive` (the provider flips an
 * *auto*-away user back to "active"). A user's *explicit* away / dnd choice is
 * left untouched — that lives in the provider, which only auto-toggles when the
 * chosen status is "active".
 *
 * "Input" is deliberately broader than the mouse alone (the headline signal):
 * keyboard, touch, and scroll count too, so someone typing a long message or
 * scrolling a thread without nudging the mouse is never wrongly marked away.
 *
 * **The DOM only sees our own window.** That is the limit of what a browser
 * can know, and it made "online" mean "using the dashboard" rather than "at
 * the computer" — someone reading email for ten minutes with the app open
 * beside it went away. The desktop shell has no such limit: it asks the OS how
 * long the whole machine has been idle and feeds the answer in through
 * {@link IdleWatcher.noteSystemIdle} (see `systemIdle.ts`), so moving the mouse
 * ANYWHERE keeps you active. A plain browser tab still only sees its own input.
 *
 * **Busy-aware.** Sitting on a call (which includes just *watching a screen
 * share* — screen-share only happens inside a call) means you are present even
 * though the mouse never moves. So the watcher takes a {@link IdleWatcher.setBusy}
 * control: while busy the idle countdown is **paused** (`onIdle` cannot fire),
 * and clearing busy restarts a **fresh full window** from that moment — so a
 * user whose call just ended gets the whole grace before going away, not an
 * instant flip. The provider derives "busy" from the active-call state
 * (`CallProvider` → `reportBusy`).
 *
 * Everything external (the timer clock, the event target) is injectable so the
 * idle/active/busy transitions are unit-testable without real DOM events or wall
 * clock — see `activity.test.ts`.
 */

declare global {
  interface Window {
    /** Test/e2e seam: shorten the auto-away window (ms) so Cypress can drive
     * the idle → away → active transition without waiting minutes. */
    __xopAutoAwayMs?: number;
  }
}

/** Inactivity window before an active user is auto-flipped to "away" (ms).
 * Five minutes balances "responsive enough to be honest" against "doesn't nag a
 * user who paused to read or is on a call". Tunable here (or per-run via
 * `window.__xopAutoAwayMs`). */
export const AUTO_AWAY_IDLE_MS = 5 * 60_000;

/** DOM events that count as user activity. `mousemove` is the headline signal
 * (the literal ask), but keyboard / touch / scroll / wheel also reset the timer
 * so an actively-working-but-mouse-still user stays "active". */
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "wheel",
  "scroll",
] as const;

/** Resolve the idle window: the per-run override wins, else the default. */
export function resolveIdleMs(): number {
  if (typeof window !== "undefined" && typeof window.__xopAutoAwayMs === "number") {
    const v = window.__xopAutoAwayMs;
    if (v > 0) return v;
  }
  return AUTO_AWAY_IDLE_MS;
}

export interface IdleWatcherOptions {
  /** Called once when the user has been idle for `idleMs`. */
  onIdle: () => void;
  /** Called once on the first activity AFTER going idle (never on plain
   * activity while already active). */
  onActive: () => void;
  /** Inactivity window in ms. Defaults to {@link resolveIdleMs}. */
  idleMs?: number;
  /** Injectable clock (tests). Defaults to `Date.now`. */
  now?: () => number;
  /** Injectable event target (tests). Defaults to `window`. */
  target?: Pick<EventTarget, "addEventListener" | "removeEventListener">;
}

/** Handle returned by {@link startIdleWatcher}. */
export interface IdleWatcher {
  /** Detach the listeners and clear the timer. */
  stop(): void;
  /**
   * Mark the user busy (in a call) or not. While busy the idle countdown is
   * **paused** — `onIdle` cannot fire, so a call (or watching a screen share)
   * never flips you to "away". Clearing busy restarts a **fresh full idle
   * window** from that moment, so a user whose call just ended gets the whole
   * grace before going away. Idempotent for a repeated value.
   */
  setBusy(busy: boolean): void;
  /**
   * Feed in the OS-reported idle time for the WHOLE COMPUTER, in seconds
   * (0 = input right now). Only ever moves the last-activity mark FORWARD, so
   * an out-of-order or stale sample can never make an active user look idle;
   * a sample newer than anything we've seen wakes an already-idle watcher
   * exactly like a real DOM event would. No-op outside the desktop shell,
   * where nothing calls it.
   */
  noteSystemIdle(seconds: number): void;
  /**
   * Record activity happening right now, for input the DOM listeners can't
   * see — chiefly a pick from the native desktop tray menu, which never
   * reaches the webview as an event but is unambiguous proof the user is
   * there. Wakes an idle watcher and restarts the countdown.
   */
  noteActivity(): void;
}

/**
 * Start watching user activity. Returns an {@link IdleWatcher} handle
 * (`stop` + `setBusy`). A no-op handle when there is no event target
 * (SSR / no `window`).
 */
export function startIdleWatcher(opts: IdleWatcherOptions): IdleWatcher {
  const idleMs = opts.idleMs ?? resolveIdleMs();
  const now = opts.now ?? (() => Date.now());
  const target =
    opts.target ?? (typeof window !== "undefined" ? (window as EventTarget) : null);
  if (!target) {
    return {
      stop: () => undefined,
      setBusy: () => undefined,
      noteSystemIdle: () => undefined,
      noteActivity: () => undefined,
    };
  }

  let idle = false;
  let busy = false;
  let last = now();
  let timer: ReturnType<typeof setTimeout> | null = null;

  function clear(): void {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function schedule(ms: number): void {
    clear();
    timer = setTimeout(tick, Math.max(0, ms));
  }

  function tick(): void {
    timer = null;
    if (busy) return; // paused — setBusy(false) restarts the countdown
    const elapsed = now() - last;
    if (elapsed >= idleMs) {
      if (!idle) {
        idle = true;
        opts.onIdle();
      }
      // Stay idle with no pending timer; the next activity event wakes us.
    } else {
      // Activity arrived while we were waiting — re-arm for the remainder.
      schedule(idleMs - elapsed);
    }
  }

  /** Record activity at `at` (ms). Wakes an idle watcher and re-arms the
   * countdown. Shared by real DOM events and OS idle samples. */
  function markActivity(at: number): void {
    last = at;
    if (idle) {
      idle = false;
      opts.onActive();
    }
    if (!busy && timer === null) schedule(idleMs - (now() - last));
  }

  const onActivity: EventListener = () => markActivity(now());

  for (const ev of ACTIVITY_EVENTS) {
    target.addEventListener(ev, onActivity, { passive: true });
  }
  schedule(idleMs);

  return {
    stop() {
      clear();
      for (const ev of ACTIVITY_EVENTS) {
        target.removeEventListener(ev, onActivity);
      }
    },
    noteActivity() {
      markActivity(now());
    },
    noteSystemIdle(seconds) {
      if (!Number.isFinite(seconds)) return;
      const at = now() - Math.max(0, seconds) * 1000;
      if (at <= last) return; // stale / out-of-order — never regress the mark
      if (idle && now() - at >= idleMs) {
        // The machine HAS seen input since our last mark, but still longer ago
        // than the whole idle window. Advance the mark quietly — waking here
        // would fire onActive only to re-fire onIdle a tick later.
        last = at;
        return;
      }
      markActivity(at);
    },
    setBusy(next) {
      if (next === busy) return;
      busy = next;
      if (busy) {
        // Pause: no away flip while on a call. We never went idle during the
        // call, so leave `idle` false and stop the countdown.
        idle = false;
        clear();
      } else {
        // Call ended — grant a fresh full window from now.
        idle = false;
        last = now();
        schedule(idleMs);
      }
    },
  };
}
